import React, { useEffect, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom"

const ToastMessage = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 2000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div className="bg-green-500 text-white px-6 py-3 rounded shadow-lg text-lg pointer-events-auto">{message}</div>
    </div>
  );
};

const UsersAdminPage = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([]);
  const [editedNames, setEditedNames] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState("");
  

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await updateDoc(doc(db, "users", userId), { isAdmin });
      setUsers((prev) => prev.map((user) => (user.id === userId ? { ...user, isAdmin } : user)));
      showToast("Admin práva byla upravena.");
    } catch (err) {
      console.error("Chyba při změně admin práv:", err);
      showToast("Nepodařilo se změnit admin oprávnění.");
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const q = query(collection(db, "users"), orderBy("email"));
        const snapshot = await getDocs(q);
        const loadedUsers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(loadedUsers);
      } catch (err) {
        console.error("Chyba při načítání uživatelů:", err);
        setError("Nepodařilo se načíst uživatele.");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const handleNameChange = (userId, value) => {
    setEditedNames((prev) => ({ ...prev, [userId]: value }));
  };

  const handleSaveName = async (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return showToast("Uživatel nenalezen.");

    const newName = (editedNames[userId] ?? user.name)?.trim();
    if (!newName) return showToast("Zadejte platné jméno.");
    if (newName === user.name) return showToast("Jméno se nezměnilo.");

    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { name: newName });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, name: newName } : u)));
      setEditedNames((prev) => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });
      showToast("Jméno bylo uloženo.");
    } catch (err) {
      console.error("Chyba při ukládání jména:", err);
      showToast("Chyba při ukládání jména.");
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    const currentUserEmail = localStorage.getItem("userEmail");
    if (userEmail === currentUserEmail) return showToast("Nemůžete smazat svůj vlastní účet.");

    if (window.confirm(`Opravdu chcete smazat uživatele ${userEmail}?`)) {
      try {
        await deleteDoc(doc(db, "users", userId));
        setUsers((prev) => prev.filter((user) => user.id !== userId));
        showToast("Uživatel byl smazán.");
      } catch (err) {
        showToast("Chyba při mazání uživatele.");
      }
    }
  };

  const filteredUsers = users.filter((u) => [u.name, u.email].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase())));

  const admins = filteredUsers.filter((u) => u.isAdmin);
  const regulars = filteredUsers.filter((u) => !u.isAdmin);

  if (loading) return <p className="p-8 text-center">Načítání...</p>;
  if (error) return <p className="p-8 text-center text-red-600">{error}</p>;

  return (
    <div className="relative">
    <div className="absolute top-4 right-4 z-50">
      
       <button className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-1" onClick={() => navigate("/admin")}>
        <ArrowLeft className="w-5 h-5" />
        Zpět</button>
       
    </div>
    <div className="p-6 max-w-5xl mx-auto relative">
     

      {toast && <ToastMessage message={toast} onClose={() => setToast("")} />}
      <h1 className="text-2xl font-bold mb-4">Správa uživatelů</h1>

      <input
        type="text"
        placeholder="Vyhledat podle jména nebo emailu..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-2 border mb-6 rounded"
      />

      {/* Admini */}
      <section>
        <h2 className="text-lg font-semibold mb-2 text-green-700">Administrátoři</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {admins.length === 0 && <p className="text-sm text-gray-500">Žádní admini.</p>}
          {admins.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              editedNames={editedNames}
              onChange={handleNameChange}
              onSave={handleSaveName}
              onDelete={handleDeleteUser}
              onToggleAdmin={handleToggleAdmin}
            />
          ))}
        </div>
      </section>

      <hr className="my-6" />

      {/* Běžní uživatelé */}
      <section>
        <h2 className="text-lg font-semibold mb-2 text-blue-700">Běžní uživatelé</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {regulars.length === 0 && <p className="text-sm text-gray-500">Žádní běžní uživatelé.</p>}
          {regulars.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              editedNames={editedNames}
              onChange={handleNameChange}
              onSave={handleSaveName}
              onDelete={handleDeleteUser}
              onToggleAdmin={handleToggleAdmin}
            />
          ))}
        </div>
      </section>
    </div>
    </div>
  );
};

const UserCard = ({ user, editedNames, onChange, onSave, onDelete, onToggleAdmin }) => (
  <div className="bg-white shadow p-4 rounded w-full h-full flex flex-col justify-between box-border">
    <div className="flex flex-col gap-2">
      <p className="text-sm text-gray-600 break-words">
        <strong>Email:</strong> {user.email}
      </p>

      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={user.isAdmin || false} onChange={(e) => onToggleAdmin(user.id, e.target.checked)} />
        Admin práva
      </label>

      <p className="text-sm text-gray-600">
        <strong>Admin:</strong> {user.isAdmin ? "Ano" : "Ne"}
      </p>

      <div className="flex gap-2 items-center flex-wrap">
        <input
          type="text"
          value={editedNames[user.id] ?? user.name ?? ""}
          onChange={(e) => onChange(user.id, e.target.value)}
          className="border p-2 rounded grow min-w-0"
        />
        <button onClick={() => onSave(user.id)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex-shrink-0">
          Uložit
        </button>
      </div>
    </div>

    <button onClick={() => onDelete(user.id, user.email)} className="text-sm text-red-600 mt-4 hover:underline">
      Smazat uživatele
    </button>
  </div>
);

export default UsersAdminPage;
